import { useForm } from "react-hook-form";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { DatosGenerales, UpdateGeneralInfoRequest } from "../../../../../types/empresa";
import { InputDatosGenerales } from "../../../../../components/ui/InputDatosGenerales";
import { getErrorMessage } from "../../../../../utils/errorHelpers";
import { ErrorMessage } from "../../../../../components/errors/ErrorMessage";
import { SwitchInput } from "../../../../../components/ui/SwitchInput";
import { useUpdateGeneralInfoMutation } from "../../../empresaApi";
import { CircularProgress } from "@mui/joy";
import { useGetFrecuenciaFilterDataQuery } from "../../../../../api/filter/dataFilterApi";
import { AvailableEmpresaFilters } from "src/utils/enums";
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import NumbersRoundedIcon from '@mui/icons-material/NumbersRounded';
import SignpostRoundedIcon from '@mui/icons-material/SignpostRounded';
import FmdGoodRoundedIcon from '@mui/icons-material/FmdGoodRounded';
import DialpadRoundedIcon from '@mui/icons-material/DialpadRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import TurnLeftRoundedIcon from '@mui/icons-material/TurnLeftRounded';
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded';
import ToggleOnRoundedIcon from '@mui/icons-material/ToggleOnRounded';
import { ActionButtons } from "../../../../../components/ui/ActionButtons";

interface Props {
    empresaId: number;
    datosGenerales: DatosGenerales;
    setShowEdit: Dispatch<SetStateAction<boolean>>;
}

export function EditDatosGenerales({ datosGenerales, setShowEdit, empresaId }: Props) {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [updateGeneralInfo, { isLoading }] = useUpdateGeneralInfoMutation();
    const [formatoMeses, setFormatoMeses] = useState<boolean>(datosGenerales.formatoMeses);
    const [frecuenciaTypeInput, setFrecuenciaTypeInput] = useState<"date" | "text">("text")
    const { data: filters, isLoading: filterLoading } = useGetFrecuenciaFilterDataQuery([
        AvailableEmpresaFilters.Estados,
        AvailableEmpresaFilters.Frecuencias,
        AvailableEmpresaFilters.Municipios,
    ]);

    const iconSize = 20;

    const {
        register,
        handleSubmit,
        setValue,
        formState: {
            errors,
            isDirty,
        }
    } = useForm<DatosGenerales>({
        defaultValues: datosGenerales
    });

    const submitUpdate = handleSubmit(async (data: DatosGenerales) => {
        try {
            setErrorMessage(null);

            const request: UpdateGeneralInfoRequest = {
                generalInfo: data,
                empresaId: empresaId 
            };

            await updateGeneralInfo(request).unwrap();

            setShowEdit(false);
        } catch (error) {
            setErrorMessage(getErrorMessage(error));
        }
    });

    useEffect(() => {
        const typeInput = formatoMeses ? "text" : "date";
        setFrecuenciaTypeInput(typeInput);
        setValue("formatoMeses", formatoMeses);
    }, [formatoMeses]);

    if(filterLoading)
        return (
        <div className="flex justify-center items-center">
            <CircularProgress />
        </div>);

    return (
        <div className="w-full h-full min-h-full">
            <form onSubmit={submitUpdate} autoComplete="off">

                <InputDatosGenerales 
                    label={"Nombre: "}
                    icon={<BusinessRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    register={register("nombre", {
                        required: "El nombre de la empresa es requerido",
                        maxLength: 40
                    })}/>

                {errors.nombre?.type === "required" && (
                    <p className="valErrorText text-sm">{errors.nombre.message}</p>
                )}
                {errors.nombre?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 40</p>
                )}

                <InputDatosGenerales 
                    label={"Sucursal: "}
                    icon={<HomeWorkRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    register={register("sucursal", {
                        maxLength: 40
                    })}/>

                {errors.sucursal?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 40</p>
                )}

                <InputDatosGenerales 
                    label={"No. Trabajadores:"}
                    icon={<Groups2RoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    type="number"
                    register={register("numeroTrabajadores", {
                        required: "El número de trabajadores es requerido"
                    })}/>

                {errors.numeroTrabajadores?.type === "required" && (
                    <p className="valErrorText text-sm">{errors.numeroTrabajadores.message}</p>
                )}

                <div className="flex items-center gap-x-2 w-full mb-[11px]">
                    <ToggleOnRoundedIcon sx={{ height: iconSize, width: iconSize }}/>
                    <label className="font-semibold min-w-[150px]">Estado</label>
                    <select
                        {...register("estadoId", {
                            required: "El estado es requerida",
                            minLength: 1,
                        })}
                        className="
                            rounded-[3px] border-gray-400 border my-1 px-2 
                            font-semibold text-gray-600 text-sm w-full h-[22px]">
                        <option hidden>Seleccionar estado</option>
                        {filters?.estadosEmpresa.map((estado, index) => (
                            <option key={index} value={estado.id}>{estado.nombre}</option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center gap-x-2 w-full mb-[11px]">
                    <UpdateRoundedIcon sx={{ height: iconSize, width: iconSize }}/>
                    <label className="font-semibold min-w-[150px]">Frecuencia de visitas</label>
                    {formatoMeses
                    ?
                    <select
                        {...register("frecuenciaVisitas", {
                            required: "La frecuencia de visita es requerida",
                            minLength: 1,
                        })}
                        className="
                            rounded-[3px] border-gray-400 border my-1 px-2 
                            font-semibold text-gray-600 text-sm w-full h-[22px]">
                        <option hidden>Seleccionar frecuencia</option>
                        {filters?.frecuencia.map((frecuencia, index) => (
                            <option key={index} value={frecuencia}>{frecuencia}</option>
                        ))}
                    </select>
                    :
                    <input
                        {...register("frecuenciaVisitas", {
                            required: "La frecuencia de visita es requerida",
                            minLength: 1,
                        })}
                        type={frecuenciaTypeInput}
                        className="
                            rounded-[3px] border-gray-400 border my-1 px-2 
                            font-semibold text-gray-600 text-sm w-full"/>
                    }
                </div>

                <div className="flex items-center gap-x-2 w-full mb-[11px]">
                    <div className="rotate-180 flex items-center">
                        <TurnLeftRoundedIcon sx={{ height: iconSize, width: iconSize }}/>
                    </div>
                    <label className="font-semibold min-w-[150px]">Formato de frecuencia</label>
                    <SwitchInput
                        setFormatoMeses={setFormatoMeses}
                        formatoMeses={formatoMeses}/>

                </div>

                <InputDatosGenerales
                    icon={<HomeRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    label={"Calle: "}
                    register={register("calle", {
                        maxLength: 50
                    })}/>
                    
                {errors.calle?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 50</p>
                )}

                <InputDatosGenerales
                    icon={<SignpostRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    label={"Colonia: "}
                    register={register("colonia", {
                        maxLength: 50
                    })}/>

                {errors.colonia?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 50</p>
                )}

                <InputDatosGenerales
                    icon={<NumbersRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    label={"No. Exterior: "}
                    register={register("numeroExterior", {
                        maxLength: 10
                    })}/>

                {errors.numeroExterior?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 10</p>
                )}

                <InputDatosGenerales
                    icon={<DialpadRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    label={"Codigo Postal: "}
                    register={register("codigoPostal", {
                        maxLength: 10
                    })}/>

                {errors.codigoPostal?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 10</p>
                )}

                <div className="flex items-center gap-x-2 w-full mb-[11px]">
                    <AccountBalanceRoundedIcon sx={{ height: iconSize, width: iconSize }}/>
                    <label className="font-semibold min-w-[150px]">Municipio:</label>
                    <select
                        {...register("municipioId", {
                            min: 1
                        })}
                        aria-invalid={errors.municipioId ? "true" : "false"}
                        className="
                            w-full h-[26px] rounded-[3px] border-gray-400 border my-1 px-2 
                            font-semibold text-gray-600 text-sm">
                        <option value={0} hidden>Seleccionar</option>
                        {filters?.municipios?.map((municipio, index) => (
                        <option value={municipio.id} key={index}>{municipio.nombre}</option>
                        ))}
                    </select>
                </div>

                {errors.municipioId?.type === "min" && (
                    <p className="valErrorText text-sm">Debe seleccionar un municipio</p>
                )}

                <InputDatosGenerales
                    icon={<FmdGoodRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    label={"Coordenadas: "}
                    register={register("coordenadas", {
                        maxLength: 50
                    })}/>

                {errors.coordenadas?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 50</p>
                )}

                {errorMessage && (
                    <div className="my-2">
                        <ErrorMessage
                        customMessage>
                            <p className="text-sm">{errorMessage}</p>
                        </ErrorMessage>
                    </div>
                )}

                <ActionButtons 
                    isLoading={isLoading}
                    disabledSaveButton={!isDirty}
                    saveButtonText="Actualizar"
                    onCancel={()=> {
                        setShowEdit(false);
                    }}
                    />
            </form>
        </div>
    );
}
